import React, { useState, useEffect, useMemo } from "react";
import "./CommitteAdmin.css";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import {
  addPanels,
  getAllPanels,
  getEmployeeList,
  addPanelMember,
  fetchPanelMembers,
  removeMember,
  updatePanel,
} from "../../../../api/ApiConfig";
import {
  FaUsers,
  FaChevronRight,
  FaSpinner,
  FaExclamationTriangle,
  FaCheckCircle,
  FaPlus,
  FaTrash,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaEdit,
} from "react-icons/fa";
import toast from "react-hot-toast";

const PREDEFINED_ROLES = [
  "Chairperson",
  "Vice Chairperson",
  "Secretary",
  "Joint Secretary",
  "Treasurer",
  "Coordinator",
  "Member",
  "Advisor",
  "Panel Head",
  "External Member",
  "Internal Auditor",
  "Other",
];

// Quill toolbar — focused on lists + basic formatting
const QUILL_MODULES = {
  toolbar: [
    [{ list: "ordered" }, { list: "bullet" }],
    ["bold", "italic", "underline"],
    [{ indent: "-1" }, { indent: "+1" }],
    ["clean"],
  ],
};

const QUILL_FORMATS = [
  "bold",
  "italic",
  "underline",
  "list",
  "bullet",
  "indent",
];

// Quill styles override to match your app's input styling
const quillStyle = {
  background: "#fff",
  borderRadius: "8px",
  fontFamily: "inherit",
};

export default function CommitteeAdmin() {
  const [formData, setFormData] = useState({
    panel_name: "",
    panel_description: "",
    panel_purpose: "",
    sop_link: "",
    dl_mail_id: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [panels, setPanels] = useState([]);
  const [showMemberModal, setShowMemberModal] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [allEmployees, setAllEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [removeReason, setRemoveReason] = useState("");
  const [loading, setLoading] = useState(true);
  const [employeeLoading, setEmployeeLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  const [rawMembers, setRawMembers] = useState([]);
  const [currentPanelMembers, setCurrentPanelMembers] = useState([]);
  const [employeeMap, setEmployeeMap] = useState({});
  const [committeeMemRol, setCommitteeMemRol] = useState("");

  const [sortConfig, setSortConfig] = useState({
    key: "panel_name",
    direction: "ascending",
  });

  const [showEditModal, setShowEditModal] = useState(null);
  const [editFormData, setEditFormData] = useState({
    panel_id: null,
    panel_name: "",
    panel_description: "",
    panel_purpose: "",
    sop_link: "",
    dl_mail_id: "",
  });
  const [deletePanelConfirm, setDeletePanelConfirm] = useState(null);

  const [showRoleModal, setShowRoleModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState("");
  const [customRole, setCustomRole] = useState("");

  useEffect(() => {
    fetchPanels();
  }, []);

  useEffect(() => {
    if (showMemberModal) {
      setSelectedEmployee(null);
      setSearchTerm("");
      setCommitteeMemRol("");
      setRawMembers([]);
      setCurrentPanelMembers([]);
      fetchAllEmployees();
      fetchRawPanelMembers(showMemberModal);
    }
  }, [showMemberModal]);

  useEffect(() => {
    if (allEmployees.length > 0) {
      const map = {};
      allEmployees.forEach((employee) => {
        const empId = getEmployeeId(employee);
        if (empId) map[empId] = employee;
      });
      setEmployeeMap(map);
    }
  }, [allEmployees]);

  useEffect(() => {
    if (rawMembers.length > 0) {
      enrichMemberData();
    } else {
      setCurrentPanelMembers([]);
    }
  }, [rawMembers, employeeMap]);

  useEffect(() => {
    if (showMemberModal) {
      let filtered = allEmployees;
      if (searchTerm.trim()) {
        const searchLower = searchTerm.toLowerCase();
        filtered = allEmployees.filter((employee) => {
          const name = getEmployeeName(employee).toLowerCase();
          const id = getEmployeeId(employee)?.toString().toLowerCase() || "";
          const department = getEmployeeDepartment(employee).toLowerCase();
          return (
            name.includes(searchLower) ||
            id.includes(searchLower) ||
            department.includes(searchLower)
          );
        });
      }
      setFilteredEmployees(filterEmployees(filtered));
    }
  }, [
    searchTerm,
    allEmployees,
    selectedEmployee,
    currentPanelMembers,
    showMemberModal,
  ]);

  const fetchAllEmployees = async () => {
    try {
      setEmployeeLoading(true);
      const response = await getEmployeeList("");
      let results = response?.data || response || [];
      setAllEmployees(Array.isArray(results) ? results : []);
    } catch (err) {
      setAllEmployees([]);
    } finally {
      setEmployeeLoading(false);
    }
  };

  const fetchRawPanelMembers = async (panelId) => {
    try {
      const response = await fetchPanelMembers({ panel_id: panelId });
      let membersData = response?.members || response?.data || response || [];
      setRawMembers(Array.isArray(membersData) ? membersData : []);
    } catch (err) {
      setRawMembers([]);
    }
  };

  const enrichMemberData = () => {
    const uniqueMembersMap = new Set();
    const enrichedMembers = [];

    rawMembers.forEach((member, index) => {
      const empId = getEmployeeId(member);
      const uniqueKey =
        member._uniqueKey || (empId ? `${empId}` : `unknown_${index}`);

      if (uniqueMembersMap.has(uniqueKey)) return;
      uniqueMembersMap.add(uniqueKey);

      let employeeData = {
        ...member,
        _uniqueKey: uniqueKey,
        member_role: getMemberRole(member),
      };

      if (empId && employeeMap[empId]) {
        employeeData = { ...employeeMap[empId], ...employeeData };
      }
      enrichedMembers.push(employeeData);
    });
    setCurrentPanelMembers(enrichedMembers);
  };

  const filterEmployees = (employees) => {
    if (!employees.length) return [];
    const existingMemberIds = new Set();
    currentPanelMembers.forEach((member) => {
      const id = getEmployeeId(member);
      if (id) existingMemberIds.add(id);
    });

    return employees.filter((employee) => {
      const employeeId = getEmployeeId(employee);
      if (selectedEmployee && getEmployeeId(selectedEmployee) === employeeId)
        return false;
      if (existingMemberIds.has(employeeId)) return false;
      return true;
    });
  };

  const fetchPanels = async () => {
    try {
      setLoading(true);
      const response = await getAllPanels();
      if (response && Array.isArray(response)) {
        const transformedPanels = response.map((panel) => ({
          id: panel.id || panel.panel_id,
          panel_name: panel.panel_name || panel.name || " ",
          panel_description:
          panel.panel_description || panel.description || " ",
          panel_purpose: panel.panel_purpose || panel.purpose || " ",
          createdAt: panel.createdAt,
          members_count: panel.members_count || 0,
          sop_link: panel.sop_link,
          dl_mail_id: panel.dl_mail_id,
        }));
        setPanels(transformedPanels);
      } else {
        setPanels([]);
      }
    } catch (err) {
      setError("Failed to load panels data");
    } finally {
      setLoading(false);
    }
  };

  const handleEmployeeSearch = (searchValue) => {
    setSearchTerm(searchValue);
    if (
      selectedEmployee &&
      !getEmployeeName(selectedEmployee)
        .toLowerCase()
        .includes(searchValue.toLowerCase())
    ) {
      setSelectedEmployee(null);
      setCommitteeMemRol("");
    }
  };

  const handleEmployeeSelect = (employee) => {
    setSelectedEmployee(employee);
    setSearchTerm(getEmployeeName(employee));
    setFilteredEmployees(filterEmployees(allEmployees));
    setIsSearchFocused(false);
    setCommitteeMemRol("");
  };

  const handleAddMember = async () => {
    if (!selectedEmployee || !showMemberModal) {
      toast.error("Please select an employee");
      return;
    }
    if (!committeeMemRol.trim()) {
      toast.error("Please select a role");
      return;
    }

    try {
      setIsSubmitting(true);
      const userData = {
        panel_id: showMemberModal,
        emp_id: getEmployeeId(selectedEmployee).toString(),
        department: getEmployeeDepartment(selectedEmployee) || "General",
        member_role: committeeMemRol.trim(),
      };
      const response = await addPanelMember(userData);
      if (response) {
        await fetchRawPanelMembers(showMemberModal);
        await fetchPanels();
        setSelectedEmployee(null);
        setSearchTerm("");
        setCommitteeMemRol("");
        toast.success(`${getEmployeeName(selectedEmployee)} added successfully!`);
      }
    } catch (err) {
      toast.error("Failed to add member.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Getters
  const getEmployeeName = (e) =>
    e?.Employee_Name || e?.name || e?.employee_name || "Unknown Employee";
  const getEmployeeDepartment = (e) =>
    e?.Name_of_Department || e?.department || "General";
  const getEmployeeId = (e) =>
    e?.emp_id || e?.id || e?.employee_id || e?.Employee_ID;
  const getMemberRole = (e) => e?.member_role || e?.role || "Member";
  const getEmployeeInitials = (e) => {
    const name = getEmployeeName(e);
    if (name === "Unknown Employee") return "EE";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .substring(0, 2);
  };
  const getEmployeeUniqueKey = (e, i = 0) =>
    e?._uniqueKey ||
    (getEmployeeId(e) ? `${getEmployeeId(e)}` : `unknown_${i}`);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitted(false);

    try {
      const response = await addPanels(formData);
      if (response) {
        await fetchPanels();
        setSubmitted(true);
        setFormData({
          panel_name: "",
          panel_description: "",
          panel_purpose: "",
          sop_link: "",
          dl_mail_id: "",
        });
        setTimeout(() => setSubmitted(false), 2000);
        toast.success("Panel created successfully!");
      }
    } catch (err) {
      toast.error("Failed to create panel.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditPanel = (panel) => {
    setShowEditModal(panel.id);
    setEditFormData({
      panel_id: panel.id,
      panel_name: panel.panel_name,
      panel_description: panel.panel_description,
      panel_purpose: panel.panel_purpose,
      sop_link: panel.sop_link,
      dl_mail_id: panel.dl_mail_id || "",
    });
  };

  const handleDeletePanel = (panel) => {
    setDeletePanelConfirm(panel);
  };

  const handleEditChange = (e) => {
    setEditFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const confirmUpdatePanel = async (e) => {
    e.preventDefault();
    try {
      setIsSubmitting(true);
      const response = await updatePanel(editFormData);
      if (response) {
        await fetchPanels();
        setShowEditModal(null);
        toast.success("Panel updated successfully!");
        setTimeout(() => setError(null), 3000);
      }
    } catch (err) {
      toast.error("Failed to update panel.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const confirmDeletePanel = async () => {
    if (!deletePanelConfirm) return;
    try {
      setIsSubmitting(true);
      const userData = {
        ...deletePanelConfirm,
        panel_id: deletePanelConfirm.id,
        status: "inactive",
      };
      const response = await updatePanel(userData);
      if (response) {
        await fetchPanels();
        setDeletePanelConfirm(null);
        toast.success("Panel deleted successfully!");
        setTimeout(() => setError(null), 3000);
      }
    } catch (err) {
      toast.error("Failed to delete panel.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemoveMember = (member, panelName) => {
    setDeleteConfirm({
      panelId: showMemberModal,
      memberId: member.member_id,
      employeeName: getEmployeeName(member),
      panelName: panelName,
      uniqueKey: getEmployeeUniqueKey(member),
    });
    setRemoveReason("");
  };

  const confirmRemoveMember = async () => {
    if (!deleteConfirm || !removeReason.trim()) return;
    try {
      setIsSubmitting(true);
      const userData = {
        member_id: deleteConfirm.memberId,
        removed_reason: removeReason.trim(),
      };
      const response = await removeMember(userData);
      if (response) {
        await fetchRawPanelMembers(showMemberModal);
        await fetchPanels();
        setDeleteConfirm(null);
        setRemoveReason("");
        toast.success(`${deleteConfirm.employeeName} removed successfully!`);
      }
    } catch (err) {
      toast.error("Failed to remove member.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const getCurrentPanel = () =>
    panels.find((panel) => panel.id === showMemberModal);
  const getPanelToEdit = () =>
    panels.find((panel) => panel.id === showEditModal);

  const sortedPanels = useMemo(() => {
    let sortableItems = [...panels];
    if (sortConfig !== null) {
      sortableItems.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key])
          return sortConfig.direction === "ascending" ? -1 : 1;
        if (a[sortConfig.key] > b[sortConfig.key])
          return sortConfig.direction === "ascending" ? 1 : -1;
        return 0;
      });
    }
    return sortableItems;
  }, [panels, sortConfig]);

  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending")
      direction = "descending";
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort style={{ opacity: 0.5 }} />;
    return sortConfig.direction === "ascending" ? <FaSortUp /> : <FaSortDown />;
  };

  return (
    <div className="cadmin-wrapper">
      {error && (
        <div
          style={{
            position: "fixed",
            top: "20px",
            right: "20px",
            zIndex: 2000,
            background: error.includes("Success") ? "#48bb78" : "#e53e3e",
            color: "white",
            padding: "15px 20px",
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          }}
        >
          {error}
        </div>
      )}

      <div className="cadmin-header-section">
        <div className="cadmin-container">
          <div className="cadmin-header">
            <h1 className="cadmin-title">Committee Management Hub</h1>
            <p className="cadmin-subtitle">
              Centralized platform for creating new panels and managing their members.
            </p>
          </div>
        </div>
      </div>

      <div className="cadmin-main-content">
        <div className="cadmin-container">

          {/* ── CREATE FORM ── */}
          <div className="cadmin-form-section">
            <div className="cadmin-form-card">
              <h2 className="cadmin-form-title">
                Create New Committees/Panels/Cohorts
              </h2>
              <form className="cadmin-form" onSubmit={handleSubmit}>
                <div className="cadmin-form-grid">

                  <div className="cadmin-form-group">
                    <label className="cadmin-label">
                      Name <span style={{ color: "red" }}>*</span>
                    </label>
                    <input
                      type="text"
                      name="panel_name"
                      value={formData.panel_name}
                      onChange={handleChange}
                      className="cadmin-input"
                      placeholder="e.g., Executive Committee"
                      required
                    />
                  </div>

                  <div className="cadmin-form-group">
                    <label className="cadmin-label">
                      Description <span style={{ color: "red" }}>*</span>
                    </label>
                    <input
                      type="text"
                      name="panel_description"
                      value={formData.panel_description}
                      onChange={handleChange}
                      className="cadmin-input"
                      placeholder="Description"
                      required
                    />
                  </div>

                  <div className="cadmin-form-group">
                    <label className="cadmin-label">
                      SOP Link
                    </label>
                    <input
                      type="text"
                      name="sop_link"
                      value={formData.sop_link}
                      onChange={handleChange}
                      className="cadmin-input"
                      placeholder="https://..."
                    />
                  </div>

                  {/* ── NEW FIELD: DL (Create) ── */}
                  <div className="cadmin-form-group">
                    <label className="cadmin-label">
                      DL
                    </label>
                    <input
                      type="text"
                      name="dl_mail_id"
                      value={formData.dl_mail_id}
                      onChange={handleChange}
                      className="cadmin-input"
                      placeholder="e.g., panel-dl@example.com"
                    />
                  </div>

                  {/* ── RICH TEXT: Purpose & Objectives (Create) ── */}
                  <div
                    className="cadmin-form-group"
                    style={{ gridColumn: "1 / -1" }}
                  >
                    <label className="cadmin-label">
                      Purpose & Objectives <span style={{ color: "red" }}>*</span>
                    </label>
                    <ReactQuill
                      theme="snow"
                      value={formData.panel_purpose}
                      onChange={(value) =>
                        setFormData((prev) => ({ ...prev, panel_purpose: value }))
                      }
                      modules={QUILL_MODULES}
                      formats={QUILL_FORMATS}
                      placeholder="1. First objective&#10;2. Second objective..."
                      style={quillStyle}
                    />
                  </div>

                </div>

                <button
                  type="submit"
                  className={`cadmin-submit-btn ${
                    isSubmitting ? "cadmin-submit-btn--loading" : ""
                  } ${submitted ? "cadmin-submit-btn--success" : ""}`}
                  disabled={isSubmitting}
                  style={{ marginTop: "16px" }}
                >
                  {isSubmitting ? (
                    <><FaSpinner className="cadmin-spinner" /> Creating Panel...</>
                  ) : submitted ? (
                    <><FaCheckCircle /> Created</>
                  ) : (
                    "Create"
                  )}
                </button>
              </form>
            </div>
          </div>

          {/* ── TABLE ── */}
          <div className="cadmin-table-section">
            <div className="cadmin-table-header">
              <h2 className="cadmin-panel-list-title">
                Existing Committees/Panels/Cohorts
              </h2>
            </div>
            <div className="cadmin-table-container">
              <table className="cadmin-panel-table">
                <thead>
                  <tr>
                    <th onClick={() => requestSort("panel_name")}>
                      Name {getSortIcon("panel_name")}
                    </th>
                    <th onClick={() => requestSort("members_count")}>
                      Members {getSortIcon("members_count")}
                    </th>
                    {/* Note: DL Column removed from View */}
                    <th onClick={() => requestSort("panel_description")}>
                      Description {getSortIcon("panel_description")}
                    </th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="4" style={{ textAlign: "center", padding: "20px" }}>
                        Loading...
                      </td>
                    </tr>
                  ) : panels.length === 0 ? (
                    <tr>
                      <td colSpan="4" style={{ textAlign: "center", padding: "20px" }}>
                        No Panels Created Yet
                      </td>
                    </tr>
                  ) : (
                    sortedPanels.map((panel) => (
                      <tr key={panel.id}>
                        <td className="cadmin-table-panel-name">
                          {panel.panel_name}
                        </td>
                        <td className="cadmin-table-members">
                          {panel.members_count || 0}
                        </td>
                        <td className="cadmin-table-description">
                          {panel.panel_description}
                        </td>
                        <td className="d-flex">
                          <button
                            className="cadmin-table-action-btn"
                            onClick={() => handleEditPanel(panel)}
                            style={{
                              padding: "10px 20px",
                              backgroundColor: "#6a67f7",
                            }}
                          >
                            <FaEdit className="mb-1" />
                          </button>
                          <button
                            className="cadmin-table-manage-btn"
                            onClick={() => setShowMemberModal(panel.id)}
                            style={{
                              marginLeft: "8px",
                              backgroundColor: "#59AC77",
                            }}
                          >
                            <FaUsers />
                          </button>
                          <button
                            className="cadmin-table-action-btn"
                            onClick={() => handleDeletePanel(panel)}
                            style={{
                              marginLeft: "8px",
                              backgroundColor: "#e74c3c",
                            }}
                          >
                            <FaTrash className="mb-1" />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* ── MEMBER MODAL ── */}
      {showMemberModal && (
        <div className="cadmin-modal-overlay mt-4">
          <div className="cadmin-modal">
            <div className="cadmin-modal-header">
              <h3>
                Manage Committees/Panels/Cohorts Members —{" "}
                {getCurrentPanel()?.panel_name}
              </h3>
              <button
                className="cadmin-modal-close"
                onClick={() => setShowMemberModal(null)}
              >
                &times;
              </button>
            </div>
            <div className="cadmin-modal-body">
              <div className="cadmin-search-add-section">
                <div className="cadmin-search-container">
                  <label className="cadmin-label">
                    Search Employees to Add
                  </label>
                  <div
                    className="cadmin-typeahead-wrapper"
                    style={{ position: "relative" }}
                  >
                    <input
                      type="text"
                      placeholder="Search by name, ID, or department..."
                      value={searchTerm}
                      onChange={(e) => handleEmployeeSearch(e.target.value)}
                      onFocus={() => setIsSearchFocused(true)}
                      onBlur={() =>
                        setTimeout(() => setIsSearchFocused(false), 200)
                      }
                      className="cadmin-input"
                    />
                    {isSearchFocused && searchTerm.length > 0 && (
                      <div className="cadmin-search-dropdown">
                        {employeeLoading ? (
                          <div style={{ padding: "20px", textAlign: "center" }}>
                            <FaSpinner className="cadmin-spinner" />{" "}
                            Loading employees...
                          </div>
                        ) : filteredEmployees.length > 0 ? (
                          filteredEmployees.map((employee, index) => (
                            <div
                              key={getEmployeeUniqueKey(employee, index)}
                              className="cadmin-search-result"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                handleEmployeeSelect(employee);
                              }}
                            >
                              <div className="cadmin-employee-avatar-lg">
                                {getEmployeeInitials(employee)}
                              </div>
                              <div>
                                <span className="cadmin-employee-name-lg">
                                  {getEmployeeName(employee)}
                                </span>
                                <div className="cadmin-employee-meta">
                                  ID: {getEmployeeId(employee)} | Dept:{" "}
                                  {getEmployeeDepartment(employee)}
                                </div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div style={{ padding: "20px", textAlign: "center" }}>
                            No results found
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {selectedEmployee && (
                  <div className="cadmin-add-member-action">
                    <div className="cadmin-selected-employee-preview">
                      <div
                        className="cadmin-employee-avatar-lg"
                        style={{ backgroundColor: "#48bb78" }}
                      >
                        {getEmployeeInitials(selectedEmployee)}
                      </div>
                      <div className="cadmin-selected-details">
                        <strong style={{ color: "var(--text-dark)" }}>
                          {getEmployeeName(selectedEmployee)}
                        </strong>
                        <span style={{ color: "var(--text-gray)" }}>
                          {getEmployeeDepartment(selectedEmployee)} (ID:{" "}
                          {getEmployeeId(selectedEmployee)})
                        </span>
                      </div>
                    </div>
                    <div
                      className="cadmin-form-group"
                      style={{ flex: "0 0 250px", marginBottom: 0 }}
                    >
                      <label className="cadmin-label">
                        Member Role *
                      </label>
                      <div
                        className="cadmin-input cadmin-role-selector"
                        onClick={() => {
                          const isPredefined =
                            PREDEFINED_ROLES.includes(committeeMemRol);
                          if (committeeMemRol && !isPredefined) {
                            setSelectedRole("Other");
                            setCustomRole(committeeMemRol);
                          } else {
                            setSelectedRole(committeeMemRol);
                            setCustomRole("");
                          }
                          setShowRoleModal(true);
                        }}
                      >
                        {committeeMemRol ? (
                          <span>{committeeMemRol}</span>
                        ) : (
                          <span style={{ color: "var(--text-gray)" }}>
                            Select a role...
                          </span>
                        )}
                        <FaSortDown
                          style={{ opacity: 0.5, marginLeft: "auto" }}
                        />
                      </div>
                    </div>
                    <button
                      className="cadmin-add-member-confirm"
                      onClick={handleAddMember}
                      disabled={isSubmitting || !committeeMemRol.trim()}
                    >
                      {isSubmitting ? (
                        <FaSpinner className="cadmin-spinner" />
                      ) : (
                        <FaPlus />
                      )}{" "}
                      Add Member
                    </button>
                  </div>
                )}
              </div>

              {/* SCROLLABLE MEMBERS LIST */}
              <div className="cadmin-current-members-section">
                <div className="cadmin-members-section-header">
                  <h4 className="cadmin-members-title">
                    Current Committees/Panels/Cohorts Members (
                    {currentPanelMembers.length || 0})
                  </h4>
                </div>
                <div className="cadmin-members-scroll-container">
                  {currentPanelMembers.length > 0 ? (
                    <div className="cadmin-members-grid">
                      {currentPanelMembers.map((member, index) => (
                        <div
                          key={getEmployeeUniqueKey(member, index)}
                          className="cadmin-member-card"
                        >
                          <div
                            className="cadmin-employee-avatar-lg"
                            style={{ backgroundColor: "var(--primary-color)" }}
                          >
                            {getEmployeeInitials(member)}
                          </div>
                          <div className="cadmin-member-details">
                            <span className="cadmin-member-name">
                              {getEmployeeName(member)}
                            </span>
                            <span className="cadmin-member-department">
                              {" "}- {getMemberRole(member)}
                            </span>
                          </div>
                          <button
                            className="cadmin-delete-btn"
                            onClick={() =>
                              handleRemoveMember(
                                member,
                                getCurrentPanel()?.panel_name
                              )
                            }
                          >
                            <FaTrash />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="cadmin-no-members">
                      <FaUsers className="cadmin-no-members-icon" />
                      <h3>No Members Added</h3>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── ROLE MODAL ── */}
      {showRoleModal && (
        <div className="cadmin-modal-overlay" style={{ zIndex: 1200 }}>
          <div
            className="cadmin-modal cadmin-modal--role"
            style={{ maxWidth: "900px", height: "auto" }}
          >
            <div className="cadmin-modal-header">
              <h3 style={{ margin: 0 }}>Select Member Role</h3>
              <button
                className="cadmin-modal-close"
                onClick={() => setShowRoleModal(false)}
              >
                &times;
              </button>
            </div>
            <div className="cadmin-modal-body cadmin-role-modal-body">
              <div className="cadmin-role-list">
                {PREDEFINED_ROLES.map((role) => (
                  <div
                    key={role}
                    className={`cadmin-role-item ${
                      selectedRole === role
                        ? "cadmin-role-item--selected"
                        : ""
                    }`}
                    onClick={() => {
                      setSelectedRole(role);
                      if (role !== "Other") setCustomRole("");
                    }}
                  >
                    {role}
                  </div>
                ))}
              </div>
              {selectedRole === "Other" && (
                <div className="cadmin-custom-role-input">
                  <label className="cadmin-label">
                    Please specify role:
                  </label>
                  <input
                    type="text"
                    value={customRole}
                    onChange={(e) => setCustomRole(e.target.value)}
                    className="cadmin-input"
                    autoFocus
                  />
                </div>
              )}
            </div>
            <div
              className="cadmin-modal-actions"
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "15px",
                padding: "20px 30px 30px",
                borderTop: "1px solid #f1f5f9",
              }}
            >
              <button
                className="cadmin-modal-btn--cancel"
                onClick={() => setShowRoleModal(false)}
                style={{ padding: "10px 20px", borderRadius: "25px", border: "none" }}
              >
                Cancel
              </button>
              <button
                className="cadmin-modal-btn--confirm"
                onClick={() => {
                  setCommitteeMemRol(
                    selectedRole === "Other" ? customRole.trim() : selectedRole
                  );
                  setShowRoleModal(false);
                }}
                disabled={
                  !selectedRole ||
                  (selectedRole === "Other" && !customRole.trim())
                }
                style={{
                  padding: "10px 20px",
                  borderRadius: "25px",
                  background: "#48bb78",
                  color: "white",
                  border: "none",
                }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── EDIT MODAL ── */}
      {showEditModal && (
        <div className="cadmin-modal-overlay" style={{ zIndex: 1200 }}>
          <div
            className="cadmin-modal"
            style={{ maxWidth: "650px", width: "90%", maxHeight: "90vh", display: "flex", flexDirection: "column" }}
          >
            <div
              className="cadmin-modal-header"
              style={{ padding: "20px 30px", borderBottom: "1px solid #f1f5f9", flexShrink: 0 }}
            >
              <h3 style={{ margin: 0 }}>Edit: {getPanelToEdit()?.panel_name}</h3>
              <button
                className="cadmin-modal-close"
                onClick={() => setShowEditModal(null)}
              >
                &times;
              </button>
            </div>
            <form onSubmit={confirmUpdatePanel} style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
              <div className="cadmin-edit-modal-body">
                <div className="cadmin-form-group">
                  <label className="cadmin-label">Name *</label>
                  <input
                    name="panel_name"
                    value={editFormData.panel_name}
                    onChange={handleEditChange}
                    className="cadmin-input"
                    required
                  />
                </div>

                <div className="cadmin-form-group">
                  <label className="cadmin-label">Description *</label>
                  <input
                    name="panel_description"
                    value={editFormData.panel_description}
                    onChange={handleEditChange}
                    className="cadmin-input"
                    required
                  />
                </div>

                <div className="cadmin-form-group">
                  <label className="cadmin-label">SOP Link</label>
                  <input
                    name="sop_link"
                    value={editFormData.sop_link || ""}
                    onChange={handleEditChange}
                    className="cadmin-input"
                    placeholder="https://..."
                  />
                </div>

                {/* ── NEW FIELD: DL (Edit) ── */}
                <div className="cadmin-form-group">
                  <label className="cadmin-label">DL</label>
                  <input
                    name="dl_mail_id"
                    value={editFormData.dl_mail_id || ""}
                    onChange={handleEditChange}
                    className="cadmin-input"
                    placeholder="e.g., panel-dl@example.com"
                  />
                </div>

                {/* ── RICH TEXT: Purpose & Objectives (Edit) ── */}
                <div className="cadmin-form-group">
                  <label className="cadmin-label">
                    Purpose & Objectives *
                  </label>
                  <ReactQuill
                    theme="snow"
                    value={editFormData.panel_purpose}
                    onChange={(value) =>
                      setEditFormData((prev) => ({
                        ...prev,
                        panel_purpose: value,
                      }))
                    }
                    modules={QUILL_MODULES}
                    formats={QUILL_FORMATS}
                    style={quillStyle}
                  />
                </div>
              </div>

              <div
                className="cadmin-modal-actions"
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "15px",
                  padding: "20px 30px",
                  borderTop: "1px solid #f1f5f9",
                  flexShrink: 0
                }}
              >
                <button
                  type="button"
                  onClick={() => setShowEditModal(null)}
                  style={{ padding: "10px 20px", borderRadius: "25px", border: "none" }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    padding: "10px 20px",
                    borderRadius: "25px",
                    background: "#48bb78",
                    color: "white",
                    border: "none",
                  }}
                >
                  {isSubmitting ? (
                    <><FaSpinner className="cadmin-spinner" /> Updating...</>
                  ) : (
                    "Update"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── DELETE PANEL MODAL ── */}
      {deletePanelConfirm && (
        <div
          className="cadmin-modal-overlay"
          style={{ zIndex: 1200, padding: 20 }}
        >
          <div
            className="cadmin-modal"
            style={{ maxWidth: "450px", width: "90%", height: "auto" }}
          >
            <div
              className="cadmin-modal-header"
              style={{
                padding: "20px 30px",
                borderBottom: "1px solid #f1f5f9",
                backgroundColor: "#fee2e2",
              }}
            >
              <h3 style={{ margin: 0, color: "#991b1b" }}>
                <FaExclamationTriangle style={{ marginRight: "8px" }} />
                Confirm Deletion
              </h3>
            </div>
            <div className="cadmin-modal-body">
              <p style={{ textAlign: "center", padding: 20 }}>
                Are you sure you want to DELETE:{" "}
                <strong>{deletePanelConfirm.panel_name}</strong>?
              </p>
            </div>
            <div
              className="cadmin-modal-actions"
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "15px",
                padding: "0 30px 30px",
              }}
            >
              <button
                onClick={() => setDeletePanelConfirm(null)}
                style={{ padding: "10px 20px", borderRadius: "25px", border: "none" }}
              >
                No, Cancel
              </button>
              <button
                onClick={confirmDeletePanel}
                style={{
                  padding: "10px 20px",
                  borderRadius: "25px",
                  background: "#e53e3e",
                  color: "white",
                  border: "none",
                }}
              >
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── DELETE MEMBER MODAL ── */}
      {deleteConfirm && (
        <div className="cadmin-modal-overlay" style={{ zIndex: 1200 }}>
          <div className="cadmin-modal cadmin-modal--delete-member-small">
            <div className="cadmin-modal-header">
              <h3 className="cadmin-delete-title">Remove Member</h3>
            </div>

            <div className="cadmin-modal-body cadmin-delete-body-small">
              <p className="cadmin-delete-message">
                Do you want to remove{" "}
                <strong>{deleteConfirm.employeeName}</strong>?
              </p>

              <div className="cadmin-form-group">
                <label className="cadmin-label">Reason *</label>
                <input
                  type="text"
                  value={removeReason}
                  onChange={(e) => setRemoveReason(e.target.value)}
                  className="cadmin-input cadmin-delete-input"
                  placeholder="Reason for removal"
                  required
                />
              </div>
            </div>

            <div className="cadmin-modal-actions cadmin-delete-actions-small">
              <button
                className="cadmin-delete-cancel"
                onClick={() => setDeleteConfirm(null)}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                className="cadmin-delete-confirm"
                disabled={!removeReason.trim() || isSubmitting}
                onClick={confirmRemoveMember}
              >
                {isSubmitting ? (
                  <><FaSpinner className="cadmin-spinner" /> Removing...</>
                ) : (
                  "Remove"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}